import { applyMiddleware, compose, createStore } from 'redux'
import { createBrowserHistory } from 'history'
import { routerMiddleware } from 'connected-react-router'
import { createLogger } from 'redux-logger'
import createRootReducer from './reducers'
import createSagaMiddleware from 'redux-saga'
import rootSaga from './sagas'
import { createTransform, persistReducer } from 'redux-persist'
import storage from 'redux-persist/lib/storage'
const { composeWithDevTools } = require('redux-devtools-extension')

const history = createBrowserHistory()
const middlewareHistory = routerMiddleware(history)
const sagaMiddleware = createSagaMiddleware()

const middlewares = [middlewareHistory]

if (
    process.env.REACT_APP_REDUX_ENVIRONMENT === 'development' ||
    process.env.REACT_APP_REDUX_ENVIRONMENT === 'localhost'
) {
    const reduxLogger = createLogger()
    middlewares.push(reduxLogger)
}
middlewares.push(sagaMiddleware)

/**
 * Flags that only mean anything while a request is in flight.
 *
 * The whole store is persisted, so a request that never finished - the tab was closed mid-flight,
 * or a 401 bounced the user to login - writes `true` to localStorage and brings it back on the next
 * visit. The button it drives then spins forever, with no request behind it, and a reload does not
 * help because the reload is what restores it.
 *
 * Cleared on the way out of storage rather than blacklisting the whole slice, so everything else in
 * it still persists. Add to this list rather than reaching for a component level workaround.
 */
const inFlightFlagsByReducer = {
    organization: [
        'courseStatsOverviewLoading',
        'courseStatsTopicTrendLoading',
        'courseStatsFilterLoading',
        'courseStatsExporting',
        'isFetchingOrganizationUsers',
        'isExportingOrgExcelReport',
        'isExportingCourseGroupProgression',
        'isExportingStudentProgression',
        'isExportingClassroomProgression',
        'fetchingCourseGroups',
        'isUploadingUserImport',
        'isDownloadingImportTemplate',
        'isFetchingEmailPreview',
        'isSendingOrganizationEmail',
    ],
}

const clearInFlightFlags = createTransform(
    (inboundState) => inboundState,
    (outboundState, key) => {
        const flags = inFlightFlagsByReducer[key]
        if (!flags || !outboundState) {
            return outboundState
        }

        const cleared = { ...outboundState }
        flags.forEach((flag) => {
            if (cleared[flag] === true) {
                cleared[flag] = false
            }
        })
        return cleared
    }
)

const persistConfig = {
    key: 'root',
    storage,
    blacklist: ['studentCourse'], // blob URLs (imageCache, audioCache) don't survive page reloads
    transforms: [clearInFlightFlags],
}

const persistedReducer = persistReducer(persistConfig, createRootReducer(history))

export default function configureStore(preloadedState) {
    const store = createStore(persistedReducer, preloadedState, composeWithDevTools(applyMiddleware(...middlewares)))

    sagaMiddleware.run(rootSaga)

    return store
}
